import React, { useState, useEffect } from "react";
import { Menu, X, Home } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex" />;
  }
  return <nav className="hidden lg:flex items-center gap-8">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-[11px] uppercase tracking-[0.15em] font-sans font-semibold text-foreground/70 hover:text-primary transition-colors duration-300" data-index={i}>
          {item.label}
        </Link>)}
    </nav>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  return <div data-section-id="26139" className={cn("fixed inset-0 z-40 bg-background/95 backdrop-blur-xl flex flex-col items-center justify-center transition-all duration-500 ease-in-out lg:hidden", isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none")}>
      <nav className="flex flex-col items-center gap-10">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-3xl font-serif text-primary hover:text-accent transition-colors" onClick={onClose} data-index={i}>
            {item.label}
          </Link>)}
      </nav>
    </div>;
}
function HeaderContent() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    loading
  } = useMenu();
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);
  return <div className="container mx-auto px-6 h-20 flex items-center justify-between relative">
      <Link to="/" className="z-50 relative shrink-0 flex items-center gap-2">
        <Home className="h-7" aria-label="Home" /> Home
      </Link>

      {!loading && <div className="hidden lg:contents">
           <DesktopMenu />
        </div>}

      

      <div className="lg:hidden z-50">
        <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="hover:bg-transparent text-primary" aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </Button>
      </div>

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="navigation-header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b", isScrolled ? "bg-background/90 backdrop-blur-lg border-border/50 shadow-sm" : "bg-background border-transparent")}>
      <MenuProvider menu_id="44">
        <HeaderContent />
      </MenuProvider>
    </header>;
}